import React from 'react'
import classnames from 'classnames'
import { ConfigurationContext } from './configuration-context'
import { getClassNames } from '../../../_utils/classnames'
import { JourneyEditableState } from '@pushly/models/lib/enums/journeys'
import { BASE_TIME_FORMAT_WITHOUT_TZ, SHORT_DATE_FORMAT } from '../../../constants'
import moment from 'moment-timezone'
import { Input, Well } from '@pushly/aqe/lib/components'
import { HistoryOutlined, InfoCircleOutlined, PlayCircleOutlined } from '@ant-design/icons'
import { Form, DatePicker, Radio, Select, Skeleton, Switch, Tooltip } from 'antd'
import { delay } from '../../../_utils/utils'
import { RadioChangeEvent } from 'antd/lib/radio'
import { Moment } from 'moment'
import { getSupportedTimezones } from '../../../_utils/moment'
import { generateDefaultSubscriberFcap } from '../helpers/journey'
import { BetterTimePicker } from '@pushly/aqe/lib/components/better-timepicker/better-timepicker'
import { TriggerType } from '@pushly/models/lib/enums'
import { ExitBehavior, ExitStatus, JourneyType, StepType } from '@pushly/models/lib/enums/journeys'
import type { ITriggerStepConfiguration, IJourneyStep } from '@pushly/models/lib/structs/journeys'
import { Journey, JourneyRevision, getJourneyEndBehavior, encodeEndBehaviorValue, decodeEndBehaviorValue } from '../../../features/journeys/types/journey-api-response'

interface IGenericDetailsWell {}

interface IState {
    name?: string
}

export class GenericDetailsWell extends React.Component<IGenericDetailsWell, IState> {
    public static contextType = ConfigurationContext
    public context!: React.ContextType<typeof ConfigurationContext>

    public state: IState = {}

    protected nameRef: { input: HTMLInputElement } | null = null

    public render() {
        const { mode, loading, journey, domain, editableState, flags } = this.context
        const journeyType = journey.type
        const config = journey.configuration
        const isJourneyEditable = editableState === JourneyEditableState.EDITABLE
        const isJourneyCompleted = editableState === JourneyEditableState.COMPLETED

        const isRecurring = journeyType === JourneyType.RECURRING

        const timezone = 'UTC' // forced UTC for working with date strings only
        const now = moment()
        const today = now.clone().startOf('day')
        const defaultEndDateValue = moment(journey.date_start).add(1, 'day').startOf('day')
        const endDateEnabled = flags.endDateEnabled ?? !!journey.date_end

        const tzOptions = getSupportedTimezones()
        const dateTimeZone = journey.date_time_zone ?? 'STZ'
        const runStrategy = dateTimeZone === 'STZ' ? 'STZ' : 'FIXED'
        const dateStart = moment(journey.date_start)
        const dateStartStr = dateStart.format(isRecurring ? SHORT_DATE_FORMAT : BASE_TIME_FORMAT_WITHOUT_TZ)
        const dateEnd = !journey.date_end ? null : moment(journey.date_end)
        const dateEndStr = !dateEnd
            ? null
            : dateEnd.format(isRecurring ? SHORT_DATE_FORMAT : BASE_TIME_FORMAT_WITHOUT_TZ)

        return (
            <Well
                className={getClassNames(null, 'journey-config-well', 'nested', `mode-${mode}`)}
                title="Details"
                hideFooter={true}
            >
                <Skeleton
                    loading={loading}
                    active={true}
                    title={false}
                    paragraph={{
                        rows: 1,
                        width: '100%',
                    }}
                >
                    <Well mode="ghost" title="Journey Type" hideFooter={true}>
                        <Radio.Group
                            className="journey-type-selection"
                            size="small"
                            buttonStyle="solid"
                            value={journeyType}
                            onChange={this.handleTypeChange}
                            disabled={!!journey.id}
                        >
                            <Radio.Button value={JourneyType.TRIGGER}>
                                <PlayCircleOutlined />
                                <span>Triggered</span>
                            </Radio.Button>
                            <Radio.Button value={JourneyType.RECURRING}>
                                <HistoryOutlined />
                                <span>Scheduled</span>
                            </Radio.Button>
                        </Radio.Group>
                    </Well>

                    {!!journeyType && (
                        <>
                            <Well mode="ghost" title="Name" hideFooter={true}>
                                <div className="journey-name-wrapper">
                                    <Input
                                        ref={(el) => (this.nameRef = el)}
                                        className="journey-name"
                                        size="small"
                                        autoComplete="off"
                                        minChars={3}
                                        maxChars={50}
                                        charExceededMessage="Journey name must be 3-50 characters long."
                                        value={this.state.name ?? journey.name}
                                        onChange={this.handleNameChange}
                                    />
                                </div>
                            </Well>

                            <Well mode="ghost" title="Run Dates" hideFooter={true}>
                                <div className="strategy-selection">
                                    <Form.Item label="Run Strategy">
                                        <Radio.Group
                                            size="small"
                                            buttonStyle="solid"
                                            value={runStrategy}
                                            onChange={(ev) => this.handleRunStrategyChange(ev.target.value)}
                                        >
                                            <Radio.Button value="FIXED">
                                                <span>Fixed</span>
                                            </Radio.Button>
                                            <Radio.Button value="STZ">Subscriber Time Zone</Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>
                                </div>

                                <div className="run-dates-row">
                                    <Form.Item
                                        className={classnames('delivery-date-start', {
                                            'with-time': !isRecurring,
                                        })}
                                        label="Journey Start"
                                    >
                                        <div className="delivery-senddate-picker">
                                            <DatePicker
                                                className="delivery-date-picker"
                                                format="ddd, MMM DD, YYYY"
                                                placeholder="Start Date"
                                                disabled={!isJourneyEditable}
                                                disabledDate={(current) => !!current && current < today}
                                                value={!dateStart ? moment().startOf('day') : dateStart}
                                                onChange={(value) => this.handleDateChange('DateStart', value)}
                                            />

                                            {!isRecurring && (
                                                <BetterTimePicker
                                                    className="delivery-time-picker"
                                                    format="h:mm a"
                                                    use12Hours={true}
                                                    placeholder="Time"
                                                    disabled={!isJourneyEditable}
                                                    value={
                                                        !journey.date_start ? moment().startOf('day') : dateStart
                                                    }
                                                    onTimeChange={(value) => this.handleTimeChange('DateStart', value)}
                                                />
                                            )}
                                        </div>
                                    </Form.Item>

                                    <Form.Item
                                        className={classnames('delivery-date-end', {
                                            'with-time': !isRecurring,
                                        })}
                                        label={
                                            <span>
                                                <span>Journey End</span>

                                                <Switch
                                                    size="small"
                                                    disabled={isJourneyCompleted}
                                                    checked={endDateEnabled}
                                                    onChange={this.handleEndDateToggle}
                                                />
                                            </span>
                                        }
                                    >
                                        <div className="delivery-senddate-picker">
                                            <DatePicker
                                                className="delivery-date-picker"
                                                format="ddd, MMM DD, YYYY"
                                                placeholder="End Date"
                                                disabled={isJourneyCompleted || !endDateEnabled}
                                                disabledDate={(current) =>
                                                    !!dateStart && !!current && current < dateStart
                                                }
                                                value={!dateEnd ? undefined : dateEnd}
                                                onChange={(value) => this.handleDateChange('DateEnd', value)}
                                            />

                                            {!isRecurring && (
                                                <BetterTimePicker
                                                    className="delivery-time-picker"
                                                    format="h:mm a"
                                                    use12Hours={true}
                                                    placeholder="Time"
                                                    disabled={isJourneyCompleted || !endDateEnabled}
                                                    value={!dateEnd ? undefined : dateEnd}
                                                    onTimeChange={(value) => this.handleTimeChange('DateEnd', value)}
                                                />
                                            )}
                                        </div>
                                    </Form.Item>

                                    {runStrategy === 'FIXED' && (
                                        <Form.Item className="delivery-time-zone" label="Time Zone">
                                            <Select
                                                className="delivery-tz-picker"
                                                dropdownClassName="delivery-tz-picker-overlay"
                                                disabled={!isJourneyEditable}
                                                showSearch={true}
                                                placeholder="Select a time zone"
                                                value={journey.date_time_zone}
                                                onChange={this.handleTimeZoneChange}
                                            >
                                                {tzOptions.map((tz) => (
                                                    <Select.Option key={tz} value={tz}>
                                                        {tz}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}
                                </div>

                                <span className="strategy-description">
                                    {this.getStrategyDescription(
                                        runStrategy,
                                        dateTimeZone,
                                        endDateEnabled,
                                        dateStartStr,
                                        dateEndStr,
                                    )}
                                </span>
                            </Well>

                            {endDateEnabled && (
                                <Well
                                    className="end-date-behavior"
                                    mode="ghost"
                                    title="End Date Behavior"
                                    hideFooter={true}
                                >
                                    <span className="end-date-behavior-label">
                                        When the journey completes, all current active members will:
                                        <Tooltip
                                            title={
                                                <>
                                                    No additional subscribers will be added to the journey when it is
                                                    completed. Choose one of the options to determine how members who
                                                    are currently active in the journey will be treated when the
                                                    journey is complete.
                                                </>
                                            }
                                        >
                                            <InfoCircleOutlined className="info-icon" />
                                        </Tooltip>
                                    </span>

                                    <div>
                                        <Select
                                            className="end-date-behavior-select"
                                            dropdownClassName="end-date-behavior-dropdown"
                                            defaultValue={encodeEndBehaviorValue(getJourneyEndBehavior(journey))}
                                            disabled={isJourneyCompleted}
                                            onChange={this.handleEndDateBehaviorChange}
                                        >
                                            <Select.Option value={ExitBehavior.BLEED}>
                                                Continue executing steps of the journey
                                            </Select.Option>
                                            <Select.Option
                                                value={`${ExitBehavior.STOP}:${ExitStatus.COMPLETED}`}
                                            >
                                                Stop executing steps and have their status set to "Completed"
                                            </Select.Option>
                                            <Select.Option
                                                value={`${ExitBehavior.STOP}:${ExitStatus.REMOVED}`}
                                            >
                                                Stop executing steps and have their status set to "Removed"
                                            </Select.Option>
                                        </Select>
                                    </div>
                                </Well>
                            )}
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected getStrategyDescription(
        runStrategy: string,
        dateTimeZone: string,
        endDateEnabled: boolean,
        dateStartStr?: string,
        dateEndStr?: string,
    ) {
        const descriptors: string[] = [`The journey will start on ${dateStartStr}`]
        if (endDateEnabled && dateEndStr) {
            descriptors.push(`and run through ${dateEndStr}`)
        }
        if (runStrategy === 'STZ') {
            descriptors.push(`in each subscriber's time zone`)
        } else {
            descriptors.push(`in the ${dateTimeZone} time zone`)
        }

        return `${descriptors.join(' ')}.`
    }

    protected handleTypeChange = async (ev: RadioChangeEvent) => {
        const updated = structuredClone(this.context.journey)
        updated.configuration.audience = updated.configuration.audience ?? {}
        const defaultSegmentId = this.context.domainDefaultSegment?.id

        updated.configuration.audience.segment_ids = defaultSegmentId ? [defaultSegmentId] : []
        updated.configuration.audience.excluded_segment_ids = undefined

        const type = ev.target.value as JourneyType

        const triggerConfiguration: ITriggerStepConfiguration = {
            type: undefined as unknown as TriggerType,
            out_step_id: null,
        }

        const trigger: IJourneyStep = {
            id: '',
            type: StepType.TRIGGER,
            configuration: triggerConfiguration,
        }

        if (type === JourneyType.RECURRING) {
            updated.configuration.schedule = {
                time_zone: updated.date_time_zone ?? 'STZ',
                type: 'daily',
                frequency: 1,
                allowed_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
            }

            updated.configuration.audience.segment_ids = defaultSegmentId ? [defaultSegmentId] : []

            // set initial recurring trigger
            triggerConfiguration.type = TriggerType.SEGMENT_EVALUATION
            triggerConfiguration.params = {}

            updated.configuration.subscriber_fcap = generateDefaultSubscriberFcap()
        } else {
            updated.configuration.schedule = undefined
            updated.configuration.subscriber_fcap = undefined

            // set initial triggered trigger
            triggerConfiguration.type = TriggerType.SUBSCRIBED
            triggerConfiguration.params = {
                event_type: TriggerType.SUBSCRIBED,
            }
        }

        updated.type = type

        // Build revision with the trigger step
        if (!updated.revision) {
            updated.revision = { steps: [trigger] } as JourneyRevision
        } else {
            updated.revision.steps = [trigger]
        }

        this.context.setJourney(updated)
        this.context.setFlags({ criteriaEnabled: false })

        return delay(() => {
            this.nameRef?.input.focus()
        }, 0.1)
    }

    protected handleNameChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const updated = structuredClone(this.context.journey)

        const name = ev.target.value
        updated.name = name

        this.setState({ name })
        this.context.setJourney(updated, true)
    }

    protected handleRunStrategyChange = (strategy: 'FIXED' | 'STZ') => {
        const { domain } = this.context

        const updated = structuredClone(this.context.journey)
        const tz = strategy === 'STZ' ? 'STZ' : domain.timezone

        updated.date_time_zone = tz
        if (updated.type === JourneyType.RECURRING) {
            this.applyTimeZoneToRecurringSchedule(updated, tz)
        }

        this.context.setJourney(updated)
    }

    protected handleTimeZoneChange = (timeZone: string) => {
        const updated = structuredClone(this.context.journey)

        updated.date_time_zone = timeZone
        if (updated.type === JourneyType.RECURRING) {
            this.applyTimeZoneToRecurringSchedule(updated, timeZone)
        }

        this.context.setJourney(updated)
    }

    protected applyTimeZoneToRecurringSchedule(journey: Journey, timeZone: string) {
        const schedule = journey.configuration?.schedule

        if (schedule) {
            schedule.time_zone = timeZone
        }
    }

    protected handleDateChange = (handlerKey: 'DateStart' | 'DateEnd', date: Moment | null) => {
        const updated = structuredClone(this.context.journey)
        let nextDate = date

        const fieldKey = handlerKey === 'DateStart' ? 'date_start' : 'date_end'
        const currentValue = updated[fieldKey]

        if (nextDate && currentValue) {
            const prevDate = moment(currentValue)
            nextDate.hour(prevDate.hour())
            nextDate.minute(prevDate.minute())
        }

        if (nextDate && updated.type === JourneyType.RECURRING) {
            /**
             * recurring journeys are locked, in the UI, as:
             * start - YYYY-MM-DD 00:00:00
             * end   - YYYY-MM-DD 11:59:59
             *
             * this allows the journey to run through the selected end date
             */
            if (handlerKey === 'DateStart') {
                nextDate.startOf('day')
            } else {
                nextDate.endOf('day')
            }
        }

        const formattedDate = nextDate ? nextDate.format('YYYY-MM-DD HH:mm:ss') : null
        if (handlerKey === 'DateStart') {
            updated.date_start = formattedDate ?? updated.date_start
        } else {
            updated.date_end = formattedDate
        }

        this.context.setJourney(updated)
    }

    protected handleTimeChange = (handlerKey: 'DateStart' | 'DateEnd', time: Moment | null) => {
        const updated = structuredClone(this.context.journey)
        let nextDate = time

        const fieldKey = handlerKey === 'DateStart' ? 'date_start' : 'date_end'
        const currentValue = updated[fieldKey]

        if (time && currentValue) {
            nextDate = moment(currentValue)
            nextDate!.hour(time.hour())
            nextDate!.minute(time.minute())
            nextDate!.second(59) // always run through the full minute
        }

        const formattedDate = nextDate ? nextDate.format('YYYY-MM-DD HH:mm:ss') : null
        if (handlerKey === 'DateStart') {
            updated.date_start = formattedDate ?? updated.date_start
        } else {
            updated.date_end = formattedDate
        }

        this.context.setJourney(updated)
    }

    protected handleEndDateToggle = (endDateEnabled: boolean) => {
        const updated = structuredClone(this.context.journey)

        if (!endDateEnabled) {
            updated.date_end = null
            updated.configuration.end_behavior = ExitBehavior.BLEED
            updated.configuration.end_status = ExitStatus.COMPLETED
        } else {
            const seed = updated.date_start ? moment(updated.date_start) : moment()

            seed.add(1, 'day').endOf('day')
            updated.date_end = seed.format('YYYY-MM-DD HH:mm:ss')
        }

        this.context.setFlags({ endDateEnabled })
        this.context.setJourney(updated)
    }

    protected handleEndDateBehaviorChange = (endDateBehavior: string) => {
        const updated = structuredClone(this.context.journey)
        const decoded = decodeEndBehaviorValue(endDateBehavior)

        updated.configuration.end_behavior = decoded.behavior as ExitBehavior
        updated.configuration.end_status = decoded.status ?? ExitStatus.COMPLETED

        this.context.setJourney(updated)
    }
}
